// Dashboard API client (W36.0, per `docs/spec/dashboard/README.md` §5.1 + §5.2–§5.4).
//
// Types are aliases onto the generated contract where identical to it
// (`schema.ts`); the rest are still hand-written to match
// `backend/app/modules/dashboard/schemas.py`, each with a recorded reason in
// `schema-parity.test.ts`.
//
// `fetchDashboard` is the single first-paint request (spec §5.1: 首屏**只等這一個請求**).
// The per-module reads the old Dashboard made do not exist any more; that is
// the entire point of the aggregation endpoint.

import { api as openapi } from "./client";
import { assertResponseBody } from "./emptyBody";
import { ApiError } from "./pair";
import type {
  AnniversaryTodayItem as SchemaAnniversaryTodayItem,
  DailyMessageArchiveItem as SchemaDailyMessageArchiveItem,
  DailyMessageRead as SchemaDailyMessageRead,
  NotificationReadAllResponse as SchemaNotificationReadAllResponse,
} from "./schema";
import { API_REQUEST_FAILED } from "../i18n/errors";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyClient = Record<"GET" | "POST" | "PUT" | "DELETE" | "PATCH", any>;
const api = openapi as unknown as AnyClient;

// ----- Schemas (mirror backend app/modules/dashboard/schemas.py) -----------

/**
 * One member of the pair, as the hero card needs them (W36.1).
 *
 * `avatar_url` is the single name profile spec §8 B2 settled on and is `null`
 * for a member with no avatar — the card then draws the `display_name` initial
 * rather than a broken image (B4's 404 fallback covers the rest).
 */
export interface DashboardPairMember {
  user_id: string;
  display_name: string;
  avatar_url: string | null;
  role: string;
}

export interface DashboardPair {
  name: string;
  relationship_start: string;
  days_together: number;
  /** Always ordered `(owner, partner)`, so hero positions are stable. */
  members: DashboardPairMember[];
}

export type ReadState = "unread" | "read" | "updated";

export interface DashboardMessage {
  date: string | null;
  content: string | null;
  last_editor_id: string | null;
  updated_at: string | null;
  read_state: ReadState | null;
  unread_count: number;
  total_count: number;
}

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type DashboardAnniversaryTodayItem = SchemaAnniversaryTodayItem;

export interface DashboardAnniversaryNextItem {
  id: string;
  title: string;
  date: string | null;
  days_until: number;
  is_anchor: boolean;
}

export interface DashboardAnniversary {
  is_today: boolean;
  today: DashboardAnniversaryTodayItem[];
  next: DashboardAnniversaryNextItem | null;
}

export interface DashboardMoods {
  me: string | null;
  partner: string | null;
}

export interface DashboardActivityItem {
  text: string;
  at: string | null;
}

export interface DashboardActivity {
  me: DashboardActivityItem | null;
  partner: DashboardActivityItem | null;
}

/** The 6 module keys the feed draws from (W51 D-1, ADR-0160). */
export type DashboardModuleKey =
  "diary" | "anniversary" | "calendar" | "album" | "wishlist" | "daily_message";

/**
 * One event in 「最新動態」 (W51 D-1, ADR-0160).
 *
 * Replaces `DashboardModuleRow` — a **locked one-row-per-module board** that
 * could not show three diaries written this morning, because it had one diary
 * row. The shape change, not a values change, is what the user was reporting.
 *
 * `created_at` is a **write** timestamp on every module (`daily_message` sends
 * its `updated_at`, which is why its `actor_id` is the last editor). The backend
 * emits it naive-UTC — SQLite's `DateTime(timezone=True)` drops `tzinfo` — and
 * `toApiDate` is the one place that reads it as UTC.
 *
 * `actor_id` is `null` for system-generated rows (the listener-created anchor
 * anniversary has `author_id: null` by design). Those render **no chip**: the
 * alternative is crediting the viewer with something they did not do.
 */
export interface DashboardFeedItem {
  key: DashboardModuleKey;
  id: string | null;
  text: string;
  actor_id: string | null;
  created_at: string;
}

/**
 * One forecast day (W47.1).
 *
 * `date` is the **location's** local day, not the app's — `ADR-0005` locks the
 * app's 「今天」 to HKT, so the two are only the same day while the Hong Kong
 * fallback is in force.
 */
export interface DashboardWeatherDay {
  date: string;
  code: number | null;
  emoji: string;
  label: string;
  high_c: number | null;
  low_c: number | null;
  rain_probability: number | null;
}

/**
 * The dashboard's weather block (W47.1).
 *
 * `latitude`/`longitude` are the grid-cell centre the **upstream** returned, not
 * the coordinate that was requested, and `is_fallback` is what lets the card say
 * 「未授權 · 顯示香港」 instead of showing Hong Kong to someone in Vancouver.
 * `available: false` keeps the card's slot so the page does not change shape
 * mid-scroll when the third party is down.
 */
export interface DashboardWeather {
  place_label: string;
  latitude: number;
  longitude: number;
  is_fallback: boolean;
  temperature_c: number | null;
  apparent_c: number | null;
  humidity_pct: number | null;
  precipitation_mm: number | null;
  code: number | null;
  emoji: string;
  label: string;
  today_high_c: number | null;
  today_low_c: number | null;
  today_rain_probability: number | null;
  days: DashboardWeatherDay[];
  available: boolean;
  /** CC BY 4.0 requires the credit on the card; the backend carries it. */
  source: string;
}

export interface DashboardPayload {
  date: string;
  pair: DashboardPair;
  daily_message: DashboardMessage;
  anniversary: DashboardAnniversary;
  moods: DashboardMoods;
  latest_activity: DashboardActivity;
  /**
   * W51 (D-1) — newest-first cross-module event stream, max 10 rows.
   *
   * Not named `latestActivity` on purpose: that would collide with the mood
   * card's per-author diary row above. Two same-meaning names in one payload is
   * how a bug gets buried. ADR-0160.
   */
  activity_feed: DashboardFeedItem[];
  /** W47.1 — rendered second, directly after the hero. */
  weather: DashboardWeather | null;
  unread_notifications: number;
}

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type DailyMessagePayload = SchemaDailyMessageRead;

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type DailyMessageArchiveItem = SchemaDailyMessageArchiveItem;

export interface DailyMessageArchive {
  items: DailyMessageArchiveItem[];
  next_cursor: string | null;
  has_more: boolean;
  total: number;
}

export interface NotificationItem {
  id: string;
  kind: string;
  ref_date: string | null;
  editor_id: string | null;
  preview: string | null;
  /**
   * The stored facts, verbatim (W36.1 / notification spec §4 step 5). The
   * `NOTIFICATION_COPY` map reads these to build per-kind deep links and copy.
   * Unknown keys are ignored, and an unknown `kind` is not rendered at all —
   * so the backend can ship a kind without a frontend deploy and the failure
   * mode is "nothing appears", never "something raw appears".
   */
  payload: Record<string, unknown>;
  read_at: string | null;
  created_at: string;
}

/**
 * 刻意手寫：contract 的 `NotificationRead` 有五個欄位是 optional，而
 * `NotificationBell` 的文案 map 需要 `payload` 一定存在 ——
 * `NOTIFICATION_COPY` 直接拿它做 index。alias 外層會讓 `payload` 多出
 * `| undefined`，卻掛在一個承諾「完整 `NotificationItem`」的名字底下。
 * 見 `schema-parity.test.ts`。
 */
export interface NotificationList {
  items: NotificationItem[];
  unread_count: number;
}

/**
 * Result of `POST /api/v1/notifications/read-all` (ADR-0069).
 *
 * `updated` rather than a 204 so the client can tell "nothing was unread" from
 * "I cleared 12 rows" — without that it has to either disable the button on a
 * guess or fire twice to find out.
 */
export type NotificationReadAllResult = SchemaNotificationReadAllResponse;

export interface MoodPair {
  date: string;
  me: string | null;
  partner: string | null;
}

// ----- Endpoints -----------------------------------------------------------

async function call<T>(
  method: "GET" | "POST" | "PUT" | "DELETE",
  path: string,
  // openapi-fetch reads query params from `params.query` — see the note in
  // `diary.ts` CallOptions and `createFinalURL` in openapi-fetch's dist.
  opts: {
    body?: unknown;
    params?: { query: Record<string, unknown> };
    /** See `emptyBody.ts` — `false` only where the contract declares no body. */
    expectBody?: boolean;
  } = {},
): Promise<{ response: Response; data: T | null; error?: unknown }> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result = await (api as any)[method](path, opts);
  // W39.79. `error` is openapi-fetch's **already-parsed** body for a non-2xx.
  // Read through a narrowed cast rather than a second `any`, so this block does
  // not grow a disable comment per field.
  const parsed = result as { data?: T; error?: unknown };
  // See `emptyBody.ts` — `parsed.data` is undefined for any bodyless 2xx.
  if (result.response?.ok)
    assertResponseBody(result.response, parsed.data, opts.expectBody !== false);
  return {
    response: result.response,
    data: parsed.data ?? null,
    error: parsed.error,
  };
}

async function apiError(
  res: { response: Response; data: unknown; error?: unknown },
  fallbackCode: string,
): Promise<ApiError> {
  // W39.79. Read the parsed body first; `openapi-fetch` consumed the response's
  // own body (dist/index.js:244), so `clone().json()` cannot read it.
  let body: unknown = res.error ?? null;
  if (body === null) {
    try {
      body = await res.response.clone().json();
    } catch {
      body = null;
    }
  }
  const err = (body as { error?: { code?: string; message?: string } } | null)?.error;
  return new ApiError(
    res.response.status,
    err?.code ?? fallbackCode,
    err?.message ?? API_REQUEST_FAILED,
  );
}

/**
 * GET /api/v1/dashboard — the single first-paint request (spec §5.1).
 *
 * W47.1: the coordinates ride **this** request rather than opening a second one.
 * The endpoint exists so the first paint waits on exactly one request, so a
 * companion `GET /weather` would be the 4-request dashboard it replaced. Passing
 * `null` for either is not a different call — the backend reads it as
 * 「fallback 香港」 and the card labels it.
 */
export async function fetchDashboard(
  coords: { lat: number | null; lon: number | null } = { lat: null, lon: null },
): Promise<DashboardPayload> {
  const params: Record<string, unknown> = {};
  if (coords.lat !== null && coords.lon !== null) {
    params["lat"] = coords.lat;
    params["lon"] = coords.lon;
  }
  const res = await call<DashboardPayload>("GET", "/api/v1/dashboard", {
    params: { query: params },
  });
  if (!res.response.ok) throw await apiError(res, "DASHBOARD_LOAD_FAILED");
  return res.data as DashboardPayload;
}

/** True when the caller has no active pair — render the prompt, nothing else. */
export function isNoPairError(err: unknown): boolean {
  return err instanceof ApiError && err.code === "DASHBOARD_NO_PAIR";
}

/** GET /api/v1/daily-messages — review archive, cursor paginated. */
export async function listDailyMessages(
  cursor: string | null = null,
  limit = 20,
): Promise<DailyMessageArchive> {
  const params: Record<string, unknown> = { limit };
  if (cursor) params["cursor"] = cursor;
  const res = await call<DailyMessageArchive>("GET", "/api/v1/daily-messages", {
    params: { query: params },
  });
  if (!res.response.ok) throw await apiError(res, "DAILY_MESSAGE_LIST_FAILED");
  return res.data ?? { items: [], next_cursor: null, has_more: false, total: 0 };
}

/** PUT /api/v1/daily-messages/{date} — create or overwrite (both partners). */
export async function putDailyMessage(date: string, content: string): Promise<DailyMessagePayload> {
  const res = await call<DailyMessagePayload>("PUT", `/api/v1/daily-messages/${date}`, {
    body: { content },
  });
  if (!res.response.ok) throw await apiError(res, "DAILY_MESSAGE_SAVE_FAILED");
  return res.data as DailyMessagePayload;
}

/** DELETE /api/v1/daily-messages/{date} — clear the slot (spec §2.2). */
export async function deleteDailyMessage(date: string): Promise<void> {
  const res = await call<null>("DELETE", `/api/v1/daily-messages/${date}`, { expectBody: false });
  if (!res.response.ok) throw await apiError(res, "DAILY_MESSAGE_DELETE_FAILED");
}

/** POST /api/v1/daily-messages/{date}/read — mark read, returns new state. */
export async function markDailyMessageRead(date: string): Promise<DailyMessagePayload> {
  const res = await call<DailyMessagePayload>("POST", `/api/v1/daily-messages/${date}/read`);
  if (!res.response.ok) throw await apiError(res, "DAILY_MESSAGE_READ_FAILED");
  return res.data as DailyMessagePayload;
}

/** GET /api/v1/mood-checkins/{date} — both sides of the pair. */
export async function getMoodPair(date: string): Promise<MoodPair> {
  const res = await call<MoodPair>("GET", `/api/v1/mood-checkins/${date}`);
  if (!res.response.ok) throw await apiError(res, "MOOD_CHECKIN_LOAD_FAILED");
  return (res.data ?? { date, me: null, partner: null }) as MoodPair;
}

/** PUT /api/v1/mood-checkins/{date} — own mood only; no user_id by design. */
export async function putMoodCheckIn(
  date: string,
  moodCode: string,
): Promise<{ date: string; user_id: string; mood_code: string }> {
  const res = await call("PUT", `/api/v1/mood-checkins/${date}`, {
    body: { mood_code: moodCode },
  });
  if (!res.response.ok) throw await apiError(res, "MOOD_CHECKIN_SAVE_FAILED");
  return res.data as { date: string; user_id: string; mood_code: string };
}

/** GET /api/v1/notifications — inbox + unread count (header bell). */
export async function listNotifications(unreadOnly = false, limit = 20): Promise<NotificationList> {
  const res = await call<NotificationList>("GET", "/api/v1/notifications", {
    params: { query: { unread_only: unreadOnly, limit } },
  });
  if (!res.response.ok) throw await apiError(res, "NOTIFICATION_LOAD_FAILED");
  return (res.data ?? { items: [], unread_count: 0 }) as NotificationList;
}

/** POST /api/v1/notifications/read-all */
export async function markAllNotificationsRead(): Promise<NotificationReadAllResult> {
  const res = await call<NotificationReadAllResult>("POST", "/api/v1/notifications/read-all");
  if (!res.response.ok) throw await apiError(res, "NOTIFICATION_READ_ALL_FAILED");
  return res.data ?? { updated: 0 };
}

/** POST /api/v1/notifications/{id}/read */
export async function markNotificationRead(id: string): Promise<NotificationItem> {
  const res = await call<NotificationItem>("POST", `/api/v1/notifications/${id}/read`);
  if (!res.response.ok) throw await apiError(res, "NOTIFICATION_READ_FAILED");
  return res.data as NotificationItem;
}
